"use client";

import * as React from "react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import MultiCarousel from "@stadium/ui/src/components/multi-carousel";

import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { O_03, OTHER_DEFAULTS } from "@stadium/shared/src/helpers/plasmic/static-slices";

import styles from "./main.module.scss";

export interface CardData {
  image: string;
  imageAlt: string;
  cardTitle: string;
  description1?: string;
  description2?: string;
  description3?: string;
}
gsap.registerPlugin(ScrollTrigger);
interface CustomDotProps {
  carouselState?: {
    currentSlide?: number;
    deviceType?: string;
  };
  onSlideChange?: (index: number) => void;
}
const HiddenCarouselDot: React.FC<CustomDotProps> = ({ carouselState, onSlideChange }) => {
  React.useEffect(() => {
    if (carouselState?.currentSlide !== undefined && onSlideChange) {
      onSlideChange(carouselState.currentSlide);
    }
  }, [carouselState?.currentSlide, onSlideChange]);
  // Return null to not render anything
  return null;
};
// Internal card type used after transformation
interface InternalCard {
  title: string;
  image: string;
  imageAlt: string;
  paragraphs: string[];
}
export interface O03Props {
  cards?: CardData[];
  className?: string;
}
const O03: React.FC<Readonly<O03Props>> = ({ cards = OTHER_DEFAULTS[O_03].cards, className }) => {
  const { isDesktopDevice } = React.useContext(CurrentDeviceContext);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [mobileActiveIndex, setMobileActiveIndex] = React.useState(0);
  const textContainerRef = React.useRef<HTMLDivElement>(null);
  const textRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const imageRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const isAnimatingRef = React.useRef(false);
  const lastChangeTimeRef = React.useRef(0);
  // Filter out cards missing required fields and transform to internal format with paragraphs array
  const internalCards: InternalCard[] = cards
    .filter(
      (card) =>
        card.cardTitle &&
        card.image &&
        (card.description1 || card.description2 || card.description3),
    )
    .map((card) => {
      const paragraphs = [card.description1, card.description2, card.description3].filter(Boolean);
      return {
        title: card.cardTitle,
        image: card.image?.url || card.image,
        imageAlt: card.imageAlt,
        paragraphs,
      };
    });
  // Callback to update mobile carousel index
  const handleMobileSlideChange = React.useCallback((index: number) => {
    setMobileActiveIndex(index);
  }, []);
  const animateToSlide = React.useCallback(
    (newIndex: number, direction: "up" | "down") => {
      if (isAnimatingRef.current || newIndex === currentSlide) return;
      isAnimatingRef.current = true;
      const oldIndex = currentSlide;
      const tl = gsap.timeline({
        onComplete: () => {
          isAnimatingRef.current = false;
          setCurrentSlide(newIndex);
        },
      });
      // Animate old text and image out
      if (direction === "down") {
        // Scrolling down: old text moves up, old image moves down
        tl.to(
          textRefs.current[oldIndex],
          {
            y: "-100%",
            opacity: 0,
            duration: 0.6,
            ease: "power2.inOut",
          },
          0,
        );
        tl.to(
          imageRefs.current[oldIndex],
          {
            y: "100%",
            opacity: 0,
            duration: 0.6,
            ease: "power2.inOut",
          },
          0,
        );
        // Animate new text and image in
        tl.fromTo(
          textRefs.current[newIndex],
          { y: "100%", opacity: 0 },
          { y: "0%", opacity: 1, duration: 0.6, ease: "power2.inOut" },
          0.1,
        );
        tl.fromTo(
          imageRefs.current[newIndex],
          { y: "-100%", opacity: 0 },
          { y: "0%", opacity: 1, duration: 0.6, ease: "power2.inOut" },
          0.1,
        );
      } else {
        // Scrolling up: old text moves down, old image moves up
        tl.to(
          textRefs.current[oldIndex],
          {
            y: "100%",
            opacity: 0,
            duration: 0.6,
            ease: "power2.inOut",
          },
          0,
        );
        tl.to(
          imageRefs.current[oldIndex],
          {
            y: "-100%",
            opacity: 0,
            duration: 0.6,
            ease: "power2.inOut",
          },
          0,
        );
        // Animate new text and image in
        tl.fromTo(
          textRefs.current[newIndex],
          { y: "-100%", opacity: 0 },
          { y: "0%", opacity: 1, duration: 0.6, ease: "power2.inOut" },
          0.1,
        );
        tl.fromTo(
          imageRefs.current[newIndex],
          { y: "100%", opacity: 0 },
          { y: "0%", opacity: 1, duration: 0.6, ease: "power2.inOut" },
          0.1,
        );
      }
    },
    [currentSlide],
  );
  const handleWheel = React.useCallback(
    (e: WheelEvent) => {
      if (internalCards.length <= 1) return;
      // Only trigger on significant scroll (helps prevent accidental triggers)
      if (Math.abs(e.deltaY) < 10) {
        return;
      }
      const now = Date.now();
      const direction = e.deltaY > 0 ? "down" : "up";
      const canMoveDown = direction === "down" && currentSlide < internalCards.length - 1;
      const canMoveUp = direction === "up" && currentSlide > 0;
      if (!canMoveDown && !canMoveUp) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (isAnimatingRef.current || now - lastChangeTimeRef.current < 100) {
        return;
      }
      const newIndex =
        direction === "down"
          ? Math.min(currentSlide + 1, internalCards.length - 1)
          : Math.max(currentSlide - 1, 0);
      if (newIndex !== currentSlide) {
        lastChangeTimeRef.current = now;
        animateToSlide(newIndex, direction);
      }
    },
    [currentSlide, internalCards.length, animateToSlide],
  );
  React.useEffect(() => {
    if (!isDesktopDevice) return;
    const textContainer = textContainerRef.current;
    if (textContainer) {
      textContainer.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        textContainer.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel, isDesktopDevice]);
  // Set initial positions
  React.useEffect(() => {
    let index = 0;
    for (const ref of textRefs.current) {
      if (ref && index !== 0) {
        gsap.set(ref, { y: "100%", opacity: 0 });
      }
      index++;
    }
    index = 0;
    for (const ref of imageRefs.current) {
      if (ref && index !== 0) {
        gsap.set(ref, { y: "-100%", opacity: 0 });
      }
      index++;
    }
  }, []);
  return (
    <div
      className={`${styles.o03Container} ${className || ""}`}
      data-name="O-03"
      data-node-id="5908:58504"
    >
      <div className={styles.swiperContainerWrapper}>
        {!isDesktopDevice && (
          <div className={styles.container}>
            <MultiCarousel
              resolutionCountsMapping={{ mobile: 1, tablet: 1 }}
              showDots={true}
              arrows={false}
              draggable={true}
              swipeable={true}
              infinite={false}
              customDot={<HiddenCarouselDot onSlideChange={handleMobileSlideChange} />}
              renderDotsOutside={true}
            >
              {internalCards.map((card, index) => (
                <div key={card.title}>
                  <div className={styles.imageWrapper}>
                    <Multimedia
                      type="image"
                      src={card.image}
                      alt={card.imageAlt}
                      className={styles.image}
                      priority={index === 0}
                    />
                  </div>
                  {/* Content */}
                  <div className={styles.content}>
                    <div className={styles.slide}>
                      <div className={styles.slideContent}>
                        <h2 className={styles.cardTitle}>{card.title}</h2>
                        <div>
                          {card.paragraphs.map((para) => (
                            <p
                              key={`${card.title}-${para.substring(0, 50)}`}
                              className={styles.cardParagraph}
                            >
                              {para}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </MultiCarousel>
            {/* Custom Indicator */}
            <div className={styles.indicatorWrapper}>
              <div className={styles.indicatorTrack}>
                <div
                  className={styles.indicatorThumb}
                  style={{
                    width: "90px",
                    left: `calc(${(mobileActiveIndex / (internalCards.length - 1)) * 100}% - ${
                      (mobileActiveIndex / (internalCards.length - 1)) * 90
                    }px)`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {isDesktopDevice && (
          <div className={styles.desktopContainer}>
            <div className={styles.desktopWrapper}>
              {/* Image Section - Left side */}
              <div className={styles.desktopImageWrapper}>
                <div className={styles.desktopImageSticky}>
                  {internalCards.map((card, index) => (
                    <div
                      key={card.image}
                      ref={(el) => (imageRefs.current[index] = el)}
                      className={styles.desktopImageContainer}
                    >
                      <Multimedia
                        type="image"
                        src={card.image}
                        alt={card.imageAlt}
                        className={styles.desktopImage}
                        priority={index === 0}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* Content - Right side */}
              <div className={styles.desktopContentWrapper} ref={textContainerRef}>
                {internalCards.map((card, index) => (
                  <div
                    key={card.title}
                    ref={(el) => (textRefs.current[index] = el)}
                    className={styles.desktopCard}
                  >
                    <h2 className={styles.cardTitle}>{card.title}</h2>
                    <div>
                      {card.paragraphs.map((para) => (
                        <p
                          key={`${card.title}-${para.substring(0, 50)}`}
                          className={styles.cardParagraph}
                        >
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
                {/* Custom Progress Indicator */}
                <div className={styles.desktopScrollbar}>
                  <div
                    className={styles.desktopScrollbarThumb}
                    style={{
                      height: "98px",
                      top: `${(currentSlide / (internalCards.length - 1)) * (100 - 90 / 5)}%`,
                      transition: "top 0.3s ease-out",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default O03;
